import {SkeletonAvatar, SkeletonText, Stack} from '@primer/react/experimental'
import {CommentLayout} from './CommentLayout'
import {CommentsEmptyState} from './CommentsEmptyState'

/**
 * A single thread preview skeleton that mimics the ThreadPreview component structure
 */
function CommentsSkeletonItem({lines}: {lines: number}) {
  return (
    <CommentLayout
      data-testid="thread-preview-skeleton-item"
      header={
        <Stack direction="horizontal" align="center" gap="condensed">
          <SkeletonAvatar size={24} />
          <SkeletonText size="bodyMedium" />
        </Stack>
      }
    >
      <SkeletonText size="bodyMedium" lines={lines} />
    </CommentLayout>
  )
}

const lineCounts = [1, 3, 2] // repeating

/** Shown when we don't know how many comments to expect. */
const defaultSkeletonCount = 3

/** More skeletons than this would never fit on screen, so rendering them is wasted work. */
const maxSkeletonCount = 15

/**
 * Renders skeleton loading states for thread previews.
 * Used as a fallback while thread preview data is being fetched.
 */
export function CommentsSkeleton({
  reviewCommentsMode,
  count = defaultSkeletonCount,
}: {
  reviewCommentsMode?: boolean
  count?: number
}) {
  return count > 0 ? (
    <Stack gap="normal" data-testid="thread-preview-skeleton">
      <p className="sr-only">Loading comments...</p>
      {Array.from({length: Math.min(count, maxSkeletonCount)}).map((_, i) => (
        <CommentsSkeletonItem key={i} lines={lineCounts[i % lineCounts.length] ?? 2} />
      ))}
    </Stack>
  ) : (
    <CommentsEmptyState reviewCommentsMode={reviewCommentsMode} />
  )
}
